// The profile — PUT customer/me claims (or updates) this identity's
// customer row at the restaurant. The mobile number is the identity key:
// it is verified by SMS code and chosen once, when the profile is made (or
// the first order is placed); after that the name, email and marketing
// choices are edited.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { VerifyPhone } from '../../components/VerifyPhone';
import { ApiError, putMe, toE164 } from '../../lib/api';
import { verifiedPhone } from '../../lib/auth';
import { AccountHead, accountPath, useMe } from './shared';

/** What the checkout remembered on this device — a new profile starts from it. */
function remembered(): { name: string; phone: string } {
  try {
    return JSON.parse(localStorage.getItem('rp-order-details') ?? '') as { name: string; phone: string };
  } catch {
    return { name: '', phone: '' };
  }
}

export function Profile() {
  const { rid, sid } = useParams() as { rid: string; sid: string };
  const { me, setMe, error: loadError, reload } = useMe(rid);
  const [verifiedAs, setVerifiedAs] = useState<string | null>(() => verifiedPhone());
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [sms, setSms] = useState(false);
  const [mail, setMail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (me === undefined) return;
    const r = remembered();
    setName(me?.name ?? r.name ?? '');
    setPhone(me?.phoneE164 ?? r.phone ?? '');
    setEmail(me?.email ?? '');
    setSms(me?.marketingSmsOptIn ?? false);
    setMail(me?.marketingEmailOptIn ?? false);
  }, [me]);

  async function save() {
    const e164 = me?.phoneE164 ?? toE164(phone);
    if (!name.trim()) return setError('Tell us your name.');
    if (!e164) return setError('Enter a mobile number the store can reach.');
    if (!me && verifiedAs !== e164) return setError('Verify your number first.');
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      setMe(await putMe(rid, { name: name.trim(), phone: e164, email: email.trim() || null, marketingSmsOptIn: sms, marketingEmailOptIn: mail }));
      setSaved(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save your profile.');
    } finally {
      setBusy(false);
    }
  }

  const toggle = (on: boolean, set: (v: boolean) => void, label: string, text: string) => (
    <button className="optrow" aria-label={label} aria-pressed={on} onClick={() => { set(!on); setSaved(false); }}>
      <span className={'chk' + (on ? ' on' : '')} />
      {text}
    </button>
  );

  return (
    <div className="co-page">
      <AccountHead back={accountPath(rid, sid)} backLabel="Back to your account" kicker="Your account" title={me ? 'Profile' : 'Set up your profile'} />
      {loadError && <div className="co-err acct-err" role="alert">{loadError}</div>}
      {me === undefined && !loadError && <div className="loading">Looking…</div>}
      {me !== undefined && (
        <>
          <section className="co-sec" data-section="profile" data-customer={me?.customerId ?? ''}>
            <h2>About you</h2>
            <input className="co-input" placeholder="Name" aria-label="Name" autoComplete="name" value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} />
            {me?.phoneE164 ? (
              <div className="acct-fixed" data-phone={me.phoneE164}>
                <span className="acct-fixed-k">Mobile</span>
                <span className="acct-fixed-v">{me.phoneE164}</span>
              </div>
            ) : (
              <>
                <input className="co-input" placeholder="Mobile number" aria-label="Mobile number" autoComplete="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                <VerifyPhone phone={toE164(phone)} verified={!!verifiedAs && verifiedAs === toE164(phone)} onVerified={(v) => { setVerifiedAs(v.user.phoneE164); reload(); }} />
              </>
            )}
            <div className="co-hint">{me?.phoneE164 ? 'Your mobile number is how the store knows you — it stays as it is.' : 'The store knows you by your mobile number; your orders and card follow it.'}</div>
            <input className="co-input" placeholder="Email (optional)" aria-label="Email" type="email" autoComplete="email" value={email} onChange={(e) => { setEmail(e.target.value); setSaved(false); }} />
          </section>

          <section className="co-sec" data-section="marketing">
            <h2>Hearing from us</h2>
            {toggle(sms, setSms, 'Offers by SMS', 'Send me offers by SMS')}
            {toggle(mail, setMail, 'Offers by email', 'Send me offers by email')}
          </section>

          {error && <div className="co-err acct-err" role="alert">{error}</div>}
          <div className="co-placebar">
            <button className="co-place" onClick={save} disabled={busy || (!me && (!verifiedAs || verifiedAs !== toE164(phone)))}>{busy ? 'Saving…' : me ? 'Save changes' : 'Create my profile'}</button>
            {saved && <div className="co-fine" role="status" data-saved>Saved.</div>}
          </div>
        </>
      )}
    </div>
  );
}
